import KeyCode from 'core/_constants/keycode.constant';
import './accessible_calendar_grid.scss';

/**
 * @ngdoc directive
 * @name bbAccessibleGrid
 *
 * @description
 * Attribute added to the calendar element which transforms it to fully accessible grid. To navigate within accessible
 * calendar we have to use Esc, Tab, Space, enter and arrow keys.
 *
 * The focus is passed automatically although you can help it using bb-accessible-calendar-grid-escape attribute by adding it to
 * the element to which you want to pass focus.
 */

/** Grid accessibility navigator helper class. */
class GridNavigator {

    /**
     * @param element Table element bound to grid navigator.
     */
    constructor(element) {
        this.element = element;
        this.activeCellIndex = 0;
        this.accordionSlotIndex = 0;
        this.isFirstTabStop = true;
        this.listen();
    }

    onTabStop() {
        this.element.blur();

        // First tab stop is caused by handler registration.
        if (this.isFirstTabStop) {
            this.isFirstTabStop = false;

            // First tab stop has meaning for simple slot list.
            if (this.isSimpleTimeSlotList) {
                return;
            }
        }

        this.inAccordion = false;
        this.event = {};

        // Accordion based grid.
        this.cells = this.element.find('bb-time-range-accordion-group');
        if (this.cells.length > 0) {
            this.columnsNumber = this.element.find('[uib-accordion]').length;
            this.gridFocus();
        } else {
            // Simple list without accordions.
            this.cells = this.element.find('ul');
            if (this.cells.length > 0) {
                this.isSimpleTimeSlotList = true;
                this.gridFocus();
                this.accordionFocus();
            } else {
                // We assume grid is empty. Pass the focus to the first available element.
                this.activeCell = this.element.find(':first-child');
                this.activeCell.attr('tabindex', '0');
                this.activeCell.focus();
            }
        }
    }

    /**
     * Listen for navigation keys to delegate to main grid or cell-accordion navigation.
     */
    listen() {
        this.element.on('keydown', (event) => {
            this.event = event;
            this.activeKeyCode = event.keyCode;

            // Set the prevCell to this one so we can swap tab index with next one.
            this.prevCell = this.cells.eq(this.activeCellIndex);

            if (this.inAccordion) {
                this.accordionNavigation();
            } else {
                this.gridNavigation();
            }

            event.preventDefault();
            event.stopPropagation();
        });

        this.element.mousedown(() => {
            this.mousedownDetected = true;
        });

        this.element.mouseup(() => {
            this.mousedownDetected = false;
        });
    }

    clearSelection() {
        this.element.blur();

        // When there is no active cell nothing has to be set to defaults.
        if (!this.activeCell) {
            return;
        }

        // Clearing grid state after user clicked somewhere else and broke the focus.
        if (this.inAccordion) {
            // Clearing slot focus.
            const activeSlot = this.accordionSlots.eq(this.accordionSlotIndex);
            activeSlot.removeAttr('tabindex');
            activeSlot.blur();

            // Clearing slot focus related information.
            this.inAccordion = false;

            // Clearing cell focus.
            this.activeCell.removeAttr('tabindex');
            this.activeCell.blur();
        } else {
            // Clearing cell focus.
            this.activeCell.removeAttr('tabindex');
            this.activeCell.blur();
        }
    }

    /** Navigating main grid .*/
    gridNavigation() {
        // Getting the first last cell on the right.
        const endCellIndex = this.columnsNumber * 3;
        switch (this.activeKeyCode) {
            case KeyCode.RIGHT:
                if (this.activeCellIndex + 3 < endCellIndex) {
                    this.activeCellIndex += 3;
                    this.gridFocus();
                }
                break;
            case KeyCode.LEFT:
                if (this.activeCellIndex - 3 >= 0) {
                    this.activeCellIndex -= 3;
                    this.gridFocus();
                }
                break;
            case KeyCode.DOWN:
                if (this.activeCellIndex + 1 < endCellIndex) {
                    this.activeCellIndex += 1;
                    this.gridFocus();
                }
                break;
            case KeyCode.UP:
                if (this.activeCellIndex - 1 >= 0) {
                    this.activeCellIndex -= 1;
                    this.gridFocus();
                }
                break;
            case KeyCode.TAB:
            case KeyCode.ESC:
                this.gridEscape();
                break;
            case KeyCode.ENTER:
            case KeyCode.SPACEBAR:
                this.accordionFocus();
                break;
        }
    }

    /** Focus on selected grid cells. */
    gridFocus() {
        this.activeCell = this.cells.eq(this.activeCellIndex);
        if (this.prevCell) {
            this.prevCell.removeAttr('tabindex');
        }

        // If accordion is already open just go into it.
        if (this.activeCell.find('[uib-accordion-group].panel-open').length > 0) {
            this.accordionFocus({ isAccordionExpanded: true });
        } else {
            this.activeCell.attr('tabindex', '0').focus();
        }
    }

    /** Lose the focus on grid. */
    gridEscape() {
        this.activeCell.removeAttr('tabindex');
        this.activeCell.blur();

        // When we escape from the grid we want to pass the focus to another element.
        // Special bb-accessible-calendar-grid-escape attribute may be use in a template to flag such an element.
        const selector =
            ['', '[bb-accessible-calendar-grid]', '[bb-accessible-calendar-grid-escape]', 'button', 'select', 'input']
                .reduce((result, element) => {
                    return `${result}${element}[tabindex!='-1']:visible:not([disabled]), `;
                })
                .slice(0, -2);

        const elementsToPassFocus = $(selector);
        const index = elementsToPassFocus.index(this.element);
        if (this.event.shiftKey) {
            elementsToPassFocus.eq(index - 1).focus();
        } else {
            elementsToPassFocus.eq(index + 1).focus();
        }
    }

    /** Navigation in accordion mode. */
    accordionNavigation() {
        switch (this.activeKeyCode) {
            case KeyCode.UP:
            case KeyCode.DOWN: {
                const previousSlotIndex = this.accordionSlotIndex;
                if (this.activeKeyCode === KeyCode.UP) {
                    this.accordionSlotIndex = (this.accordionSlotIndex - 1) % this.accordionSlots.length;
                } else {
                    this.accordionSlotIndex = (this.accordionSlotIndex + 1) % this.accordionSlots.length;
                }

                // Go further only if slot has actually changed.
                if (previousSlotIndex !== this.accordionSlotIndex) {
                    if (this.currentSlot) {
                        this.currentSlot.removeAttr('tabindex');
                        this.currentSlot.blur();
                    }
                    this.accordionFocus();
                }
                break;
            }
            case KeyCode.ESC:
            case KeyCode.TAB:
                this.accordionEscape();
                break;
            case KeyCode.ENTER:
            case KeyCode.SPACEBAR:
                if (this.inAccordion) {
                    this.accordionSlots.eq(this.accordionSlotIndex).click();

                    if (!this.isSimpleTimeSlotList) {
                        this.accordionEscape({ slotChosen: true });
                    }
                }
                break;
        }
    }

    /** Focus on cell-accordion selection. */
    accordionFocus({ isAccordionExpanded } = {}) {
        // Find accordion element which can be toggled.
        this.accordionToggle = this.activeCell
            .find('[uib-accordion-group]:not([disabled="disabled"]) .accordion-toggle');

        // No point in focusing accordion if there is no such an element or the time slots is a simple list.
        if (this.isSimpleTimeSlotList || this.accordionToggle.length > 0) {
            if (!this.inAccordion) {
                this.inAccordion = true;

                this.activeCell = this.cells.eq(this.activeCellIndex);
                this.activeCell.removeAttr('tabindex');
                this.activeCell.blur();

                if (!isAccordionExpanded) {
                    this.accordionToggle.click();
                }
            }

            this.accordionSlots = this.cells.eq(this.activeCellIndex).find('.time-slot');
            this.currentSlot = this.accordionSlots.eq(this.accordionSlotIndex);
            this.currentSlot.attr('tabindex', '0').focus();
        }
    }

    /** Escape accordion navigation to grid. */
    accordionEscape(slotChosen) {
        const activeSlot = this.accordionSlots.eq(this.accordionSlotIndex);
        activeSlot.removeAttr('tabindex');
        activeSlot.blur();

        this.activeCell.attr('tabindex', '0');
        this.inAccordion = false;

        if (this.isSimpleTimeSlotList) {
            this.gridEscape();
        } else {
            this.accordionSlotIndex = 0;
            if (!slotChosen) {
                this.accordionToggle.click();
            }
            this.gridFocus();
        }
    }
}

function link(scope, element) {
    // Every accessible grid must be focusable.
    element.attr('tabindex', '0');

    // Grid navigator has to exist before grid was focused to handle all events from the beginning.
    const navigator = new GridNavigator(element);

    /* Activate on element focus event. */
    element.focus(() => {
        if (navigator.mousedownDetected) {
            // Do nothing beside clearing selection when focus was caused by mouse press.
            navigator.mousedownDetected = false;
            navigator.clearSelection();
        } else {
            navigator.onTabStop();
        }
    });

    const clickBinding = () => {
        if (navigator) {
            navigator.clearSelection();
        }
    };

    // Clear selection and tags that might have been added meanwhile.
    angular.element(window).mousedown(clickBinding);

    // Clearing DOM from redundant binding.
    scope.$on('$destroy', () => {
        element.off();
        angular.element(window).off('mousedown', clickBinding);
    });
}

const bbAccessibleCalendarGrid = () => {
    return {
        restrict: 'A',
        link
    };
};

angular
    .module('BBPublicBooking')
    .directive('bbAccessibleCalendarGrid', bbAccessibleCalendarGrid);


// WEBPACK FOOTER //
// node_modules/bookingbug-angular/src/public-booking/time-ranges/accessible-grid/accessible_calendar_grid.directive.js